import { useSelector } from 'react-redux'
import {
  paymentFieldScreenAndEntriesSelector,
  activeFieldInTableSelector,
  paymentFieldDataToIncludeTableSelector,
  paymentFieldDataToIncludeSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { whiteLabelSelector } from 'apps/formsCreationKit/store/selectors/settings'
import { isTable } from 'apps/builder/lib/utils'
import { WhiteLabelCheck } from 'apps/builder/components/shared'
import {
  LabelInput,
  EntryTypeSelect,
  StandardDesignConfig,
} from 'apps/studio/components/settings/fields/Base/standard'

import { SettingsLabel } from 'apps/studio/components/shared/inputs/SettingsLabel' 

const PaymentHelpTooltip = () => {
  const isWhiteLabeled = useSelector(whiteLabelSelector)
  return (
    <div>
      <p>
        {isWhiteLabeled ? 'You can use this field' : 'GoCanvas allows you'} to
        integrate with Square, which means you can immediately and easily
        collect payment using your Form and Square's supported devices.{' '}
        <span>
          {' '}
          <WhiteLabelCheck>
            <a
              className="info--block--link"
              href="https://help.gocanvas.com/hc/en-us/articles/115006629607"
              target="blank"
            >
              Learn More
            </a>
          </WhiteLabelCheck>
        </span>
      </p>
    </div>
  )
}

const PaymentTotalFieldSelect = {
  label: 'Payment Total Field',
  settingType: 'sheetEntrySelect',
  updateType: 'entry',
  property: 'dependent_entry',
  nestedProp: 'guid',
  tooltip:
    'To use a Payment field, you must first add a Number (with decimal or currency style) or Calculation field to your app.',
  dynamicChoices: () => {
    const paymentEligibleSheets = useSelector(
      paymentFieldScreenAndEntriesSelector
    )

    const options = paymentEligibleSheets.map(({ sheet, entries }) => ({
      label: sheet.description || sheet.placeholder,
      options: entries.map(({ guid, label, type }) => ({
        value: guid,
        label,
        type,
        sheet,
      })),
    }))

    return options
  },
  isDisabled: () => {
    const paymentEligibleSheets = useSelector(
      paymentFieldScreenAndEntriesSelector
    )
    return !paymentEligibleSheets.length
  },
}

const DataToIncludeSelect = {
  label: 'Data to Include',
  settingType: 'sheetEntrySelect',
  updateType: 'entry',
  property: 'summary_entry',
  nestedProp: 'guid',
  dynamicChoices: () => {
    const allSheets = useSelector(sheetsSelector)
    const isFieldInATable = useSelector(activeFieldInTableSelector)
    const dataToInclude = isFieldInATable
      ? useSelector(paymentFieldDataToIncludeTableSelector)
      : useSelector(paymentFieldDataToIncludeSelector)
    const sheetsWithEligibleEntries = allSheets.filter(({ id }) =>
      dataToInclude.some(entry => `${entry.sheet_id}` === `${id}`)
    )

    const options = sheetsWithEligibleEntries.reduce((acc, sheet) => {
      const parentSheet = allSheets.find(
        ({ multi_section }) => multi_section === sheet.section_id
      )

      const displaySheet =
        parentSheet && isTable(parentSheet) ? parentSheet : sheet

      const entries = dataToInclude.filter(
        entry => `${entry.sheet_id}` === `${sheet.id}`
      )
      return [
        ...acc,
        {
          label: displaySheet.description || displaySheet.placeholder,
          options: entries.map(({ guid, label, type }) => ({
            value: guid,
            label,
            type,
            sheet: displaySheet,
          })),
        },
      ]
    }, [])

    return options
  },
  isDisabled: ({ dependent_entry }) => {
    const isFieldInATable = useSelector(activeFieldInTableSelector)
    const dataToInclude = isFieldInATable
      ? useSelector(paymentFieldDataToIncludeTableSelector)
      : useSelector(paymentFieldDataToIncludeSelector)

    return !dataToInclude.length || !dependent_entry
  },
}

export const PaymentStandardSettings = [
  LabelInput,
  {
    ...EntryTypeSelect,
    tooltip: <PaymentHelpTooltip />,
  },
  SettingsLabel('Config'),
  PaymentTotalFieldSelect,
  DataToIncludeSelect,
  ...StandardDesignConfig,
]
